import { SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'aura-avatar',
  template: `
    <div class="relative inline-flex shrink-0 items-center justify-center group" [class]="sizeClass()">
      @if (isAdmin()) {
        <!-- Halo d'ambiance néon avec pulsation douce -->
        <div
          class="absolute -inset-1 rounded-lg bg-gradient-to-tr from-primary-9 via-violet-9 to-amber-8 opacity-45 blur-[3px] transition-all duration-300 group-hover:opacity-90 group-hover:blur-[6px] animate-pulse pointer-events-none"
        ></div>

        <!-- Faisceau lumineux tournant (Border Beam rotatif) -->
        <div class="absolute -inset-[2px] rounded-lg overflow-hidden pointer-events-none">
          <div
            class="absolute -inset-[100%] motion-safe:animate-[spin_4s_linear_infinite]"
            style="background: conic-gradient(from 0deg, transparent 0 240deg, var(--color-primary-9) 300deg, var(--color-amber-8) 345deg, #ffffff 360deg);"
          ></div>
        </div>
      }

      @if (srcImage()) {
        <img
          class="surface-default relative z-10 shrink-0 truncate rounded-md object-cover transition-transform duration-200 group-hover:scale-105"
          [class]="sizeClass()"
          src="{{ srcImage() }}"
          [width]="pixelSize()"
          [height]="pixelSize()"
          [alt]="label() + ' avatar'"
        />
      } @else {
        <span
          class="surface-default relative z-10 flex shrink-0 items-center justify-center truncate rounded-md bg-neutral-a3 font-semibold text-neutral-a11 select-none transition-transform duration-200 group-hover:scale-105"
          [class]="sizeClass()"
          [style.fontSize.px]="fontSize()"
        >
          {{ label() | slice: 0 : 1 }}
        </span>
      }
    </div>
  `,
  imports: [SlicePipe],
})
export class AvatarAuraComponent {
  srcImage = input<string | null>();
  isAdmin = input<boolean>(false);
  label = input.required<string>();
  size = input<'3xs' | '2xs' | 'xs' | 's' | 'md' | 'l' | 'xl' | '2xl' | '3xl' | 'full'>('md'); // default md

  private sizeMap = {
    '3xs': { tailwind: 'size-4', px: 16, font: 10 },
    '2xs': { tailwind: 'size-5', px: 20, font: 11 },
    xs: { tailwind: 'size-6', px: 24, font: 12 },
    s: { tailwind: 'size-8', px: 32, font: 14 },
    md: { tailwind: 'size-9', px: 36, font: 16 },
    l: { tailwind: 'size-12', px: 48, font: 20 },
    xl: { tailwind: 'size-16', px: 64, font: 28 },
    '2xl': { tailwind: 'size-20', px: 80, font: 36 },
    '3xl': { tailwind: 'size-32', px: 128, font: 52 },
    full: { tailwind: 'w-[400px] h-[400px]', px: 500, font: 64 },
  } as const;

  sizeClass = computed(() => this.sizeMap[this.size()]?.tailwind ?? 'size-9');
  pixelSize = computed(() => this.sizeMap[this.size()]?.px ?? 36);
  fontSize = computed(() => this.sizeMap[this.size()]?.font ?? 16);
}
